// apps/external/src/hooks/useAnnouncer.ts

import { useCallback, useRef, useEffect } from "react";

let announcerElement: HTMLDivElement | null = null;
let announcerRefCount = 0;

function getOrCreateAnnouncer(): HTMLDivElement {
  if (!announcerElement || !document.body.contains(announcerElement)) {
    announcerElement = document.createElement("div");
    announcerElement.setAttribute("role", "status");
    announcerElement.setAttribute("aria-live", "polite");
    announcerElement.setAttribute("aria-atomic", "true");
    announcerElement.className = "sr-only";
    document.body.appendChild(announcerElement);
  }
  return announcerElement;
}

/**
 * Cleanup announcer element when no longer needed.
 * Called automatically when all useAnnouncer hooks unmount.
 */
function cleanupAnnouncer(): void {
  if (announcerElement && document.body.contains(announcerElement)) {
    document.body.removeChild(announcerElement);
    announcerElement = null;
  }
}

/**
 * Hook for announcing messages to screen readers via live region
 *
 * Usage:
 *   const { announce } = useAnnouncer();
 *   announce('Selected 10:30 AM on Monday');
 *   announce('Error occurred', 'assertive');
 */
export function useAnnouncer() {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const announce = useCallback(
    (message: string, priority: "polite" | "assertive" = "polite") => {
      const announcer = getOrCreateAnnouncer();
      announcer.setAttribute("aria-live", priority);

      // Clear previous message to ensure re-announcement
      announcer.textContent = "";

      // Small delay to ensure screen readers pick up the change
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        announcer.textContent = message;
      }, 100);
    },
    []
  );

  // Reference counting for proper cleanup to prevent memory leaks
  useEffect(() => {
    announcerRefCount++;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      announcerRefCount--;
      // Clean up announcer element when no components are using it
      if (announcerRefCount === 0) {
        cleanupAnnouncer();
      }
    };
  }, []);

  return { announce };
}
